<template>
  <div>
    <div class="mt-10 location-lang-domain-area">
      <div>
        <div class="card-small-title mb-5">
          Select location
        </div>
        <v-autocomplete outlined dense hide-details="auto"
          :items="availableLocations"
          item-text="location_name"
          item-value="location_code"
          :value="config.wizardPOLocation"
          @input="value => setConfig('wizardPOLocation', value)"
          color="grayColor">
        </v-autocomplete>
        <div class="mt-3 d-flex align-center gap5">
          <vue-toggles
            :value="config.wizardPOIsCountryEnabled"
            @click="() => setConfig('wizardPOIsCountryEnabled', !config.wizardPOIsCountryEnabled)"
            width="40" height="20"
            :checkedBg="$vuetify.theme.currentTheme['green7Color']"
            :uncheckedBg="$vuetify.theme.currentTheme['grayColor']"
          />
          <div class="mainColor--text font-weight-bold">Enable country</div>
        </div>
      </div>
      <div>
        <div class="card-small-title mb-5">
          Select language
        </div>
        <v-autocomplete outlined dense hide-details
          :items="targetLanguages"
          :value="config.wizardPOLanguage"
          @input="value => setConfig('wizardPOLanguage', value)"
          item-text="language_name"
          item-value="language_code"
          color="grayColor">
        </v-autocomplete>
      </div>
      <div>
        <div class="card-small-title mb-5">
          Add Domain name
        </div>
        <domain-selector
          :baseDomain="campaign?.targetUrl || ''"
          :initialDomain="config.wizardPODomain || campaign?.targetUrl || config.wizardDomain"
          @onChangeDomain="(d) => {
            googleSiteUrl = d;
            setConfig('wizardPODomain', d);
          }"
        />
      </div>
      <div>
        <div class="card-small-title mb-5">
          Select period
        </div>
        <v-autocomplete outlined dense hide-details
          :items="availablePeriods"
          :value="config.wizardPOPeriod"
          @change="(value) => setConfig('wizardPOPeriod', availablePeriods.find(period => period.value === value))"
          item-text="label"
          color="grayColor">
        </v-autocomplete>
      </div>
    </div>
    <div class="mt-10 small-width-area">
      <div class="card-small-title mb-5">
        Select URLs to Analyze
      </div>
      <div>Tip: You can match URLs using wildcards (*). Insert a query (e.g. https://www.pageoptimizer.pro/blog/*), then hit <em>Enter.</em></div>
      <v-combobox
        ref="urlsCombobox"
        multiple
        outlined
        hide-details
        dense
        chips
        deletable-chips
        small-chips
        :items="config.wizardPOPageUrls"
        item-text="url"
        item-value="url"
        v-model="config.wizardPOSelectedPageUrls"
        :filter="urlInputCustomFilter"
        :search-input.sync="urlInputSearchValue"
        color="green7Color"
      >
        <template v-slot:selection="data">
          <v-chip
            class="my-2"
            v-bind="data.attrs"
            :input-value="data.selected"
            close
            @click="removeUrl(data.item, true)"
            @click:close="removeUrl(data.item)"
          >
            {{ data.item }}
          </v-chip>
        </template>
      </v-combobox>
    </div>
    <div class="mt-10 small-width-area">
      <div class="d-flex align-center gap10">
        <div class="card-small-title">
          Exclude branded keywords
        </div>
        <span class="input-count-info">{{ brandedTerms.length }} / 10</span>
        <div class="flex-grow-1 d-flex justify-end">
          <vue-toggles
            :value="config.wizardPOIsBrandedEnabled"
            @click="() => setConfig('wizardPOIsBrandedEnabled', !config.wizardPOIsBrandedEnabled)"
            width="40" height="20"
            :checkedBg="$vuetify.theme.currentTheme['green7Color']"
            :uncheckedBg="$vuetify.theme.currentTheme['grayColor']"
          />
        </div>
      </div>
      <v-textarea outlined
        color="greenColor"
        class="mt-3"
        :value="config.wizardPOBrandedText"
        @input="value => setConfig('wizardPOBrandedText', value)"
      />
    </div>
    <div class="mt-5 d-flex align-center gap10" style="position:relative;">
      <v-btn rounded
        color="profileAnchorColor"
        class="text-none whiteColor--text"
        :disabled="!canProceed || config.wizardPOIsCalculating"
        @click="onStart"
        >
        <span>Get data</span>
      </v-btn>
    </div>
  </div>
  </template>
  
<script>
import { gapi } from 'gapi-script';
import dataForSeoLocations from '@/consts/dataforseo_locations.json';
import languages from '@/consts/dataforseo_languages.json';

import DomainSelector from '@/components/Campaigns/DomainSelector.vue';
import { getCountryCode_ISO3 } from '@/utils/search-console-helper.js';
import { getPathNameFromUrl, wildcardMatchString } from "@/utils";
import { DATE_RANGE_OPTIONS, getOptionDate } from '@/components/Campaigns/keyword-wizard/DateRangeSelect.vue';
import { groupBy, sumBy, meanBy, chain } from "lodash"

let google_console_data_aggregated = []

export default {
  components: {
    DomainSelector,
  },
  props: ['siteId'],
  data () {
    return {
      googleSiteUrl: '',
      loadedGoogleConsoleDataAggregated: false,
      urlInputSearchValue: '',
    }
  },
  computed: {
    config() {
      return this.$store.getters.insightKeywordsConfig;
    },
    wizardPOIsCalculating() {
      return this.config.wizardPOIsCalculating
    },
    availableLocations () {
      return dataForSeoLocations.locations;
    },
    targetLanguages () {
      return languages;
    },
    availablePeriods () {
      return DATE_RANGE_OPTIONS
    },
    googleUser () {
      return this.$store.state.googleUser
    },
    campaign () {
      return this.$store.state.currentCampaign
    },
    canProceed () {
      return this.googleUser && this.googleSiteUrl
    },
    brandedTerms () {
      return this.config.wizardPOBrandedText.replace(/\n/g, ',').split(',').reduce((acc, cur) => {
        if (cur && cur.trim()) {
          acc.push(cur.trim())
        }
        return acc
      }, [])
    },
    wizardPOLanguage () {
      return this.config.wizardPOLanguage
    },
    wizardPOLocation () {
      return this.config.wizardPOLocation
    },
  },
  watch: {
    wizardPOIsCalculating: function(newVal) {
      this.$emit('setCalculating', newVal);
      if (!newVal) {
        if (this.config.wizardPOInterval) {
          clearInterval(this.config.wizardPOInterval)
          this.setConfig('wizardPOInterval', null)
        }
      }
    },
    urlInputSearchValue: function() {
      // on url search value change, make sure no dropdown option is highlighted
      // this is so when user hits Enter, only the search input value is selected
      // without this fix, any previously selected option, would be re-selected as well
      this.$refs.urlsCombobox.setMenuIndex(-1)
    }
  },
  methods: {
    getPathNameFromUrl,
    removeUrl(urlToRemove, keepInSearchInput = false) {
      this.setConfig('wizardPOSelectedPageUrls', this.config.wizardPOSelectedPageUrls.filter(row => urlToRemove !== row))
      if (keepInSearchInput){
        this.$nextTick().then(() => {
          this.urlInputSearchValue = urlToRemove
        })
      }
    },
    urlInputCustomFilter (item, queryText, itemText) {
      return (
        queryText.includes('*')
          ?wildcardMatchString(itemText, queryText)                             // match using wildcard
          :itemText.toLocaleLowerCase().includes(queryText.toLocaleLowerCase()) // ordinary matching
      )
    },
    setConfig(key, value) {
      this.$store.commit("setInsightKeywordsConfig", { key, value });
    },
    getConsoleDataAggregated (startRow = 0, client_side_task_id = null) {

      const rowLimit = 25000
      const range = getOptionDate(this.config.wizardPOPeriod)
      const endDate = range.endDate
      const startDate = range.startDate

      let filterList = []
      if (this.config.wizardPOIsCountryEnabled) {
          filterList.push({
              "dimension": "COUNTRY",
              "expression": getCountryCode_ISO3(this.config.wizardPOLocation)
          })
      }
      if (this.config.wizardPOIsBrandedEnabled) {
          this.brandedTerms.slice(0, 10).map((keyword) => {
              filterList.push({
                  "dimension": "QUERY",
                  "operator": "excludingRegex",
                  "expression": `(?i)${keyword}`
              })
          })
      }

      const queryParams = {
        "siteUrl": this.googleSiteUrl,
        "resource": {
            "startDate": startDate,
            "endDate": endDate,
            "dimensionFilterGroups": [
                {
                    "filters": filterList
                }
            ],
            "dimensions": [
                "QUERY",
                "PAGE"
            ],
            "rowLimit": rowLimit,
            "startRow": startRow,
        }
      }

      gapi.client.webmasters.searchanalytics.query(queryParams)
        .then((response) => {
          if (response.status == 200) {
            if (response.result?.rows) {
              google_console_data_aggregated = [...google_console_data_aggregated, ...response.result.rows]
              if (response.result?.rows.length == rowLimit) {
                  this.getConsoleDataAggregated(startRow + rowLimit + 1, client_side_task_id)
              } else {
                // format results
                let results = this.formatResults()

                // save data in backend, then show it to user
                return this.$store.dispatch('saveKeywordWizardResult', {
                  campaignId: this.campaign.id,
                  locationCode: this.config.wizardPOLocation,
                  languageCode: this.config.wizardPOLanguage,
                  tabType: 'PAGE_OPPORTUNITY',
                  result: results,
                  shouldReturnResult: true,  // because data is further updated with scores in the backend
                })
                  .then((response) => {
                    if (response.data && response.data.status == 'SUCCESS') {
                      this.setConfig('wizardPOResults', response.data.wizardResults);
                      this.setConfig('wizardPOResultsHistoryMetadata', response.data.wizardData);
                      this.setConfig('wizardPOPageUrls', chain(response.data.wizardResults).map('page').uniq().value());
                      this.setConfig('wizardPOIsCalculating', false);
                      this.$notify({
                        group: 'info', type: 'success',
                        text: 'Successfully calculated all the data.'
                      })
                      this.$store.commit('updateClientSideAsyncTaskStatus', {
                        'id': client_side_task_id,
                        'task_status': {
                          'status': 'SUCCESS',
                          'msg': 'Completed',
                          'value': 100
                        },
                      })
                    } else {
                      this.setConfig('wizardPOIsCalculating', false)
                      this.$notify({
                        group: 'info', type: 'error',
                        text: 'Could not save results.'
                      })
                      this.$store.commit('updateClientSideAsyncTaskStatus', {
                        'id': client_side_task_id,
                        'task_status': {
                          'status': 'FAILURE',
                          'value': 100,
                          'msg': 'Could not save results.'
                        },
                      })
                    }
                  })
              }
            }
          } else {
            this.$notify({
              group: 'info', type: 'error',
              text: 'no history found'
            });
            this.$store.commit('updateClientSideAsyncTaskStatus', {
              'id': client_side_task_id,
              'task_status': {
                'status': 'FAILURE',
                'value': 100,
                'msg': 'no history found',
              },
            })
          }
        },
        (err) => {
          // handlePause()
          console.error("Execute error", err);
          this.$notify({
            group: 'info', type: 'error',
            text: 'Failed to get analystic data'
          });
          this.$store.commit('updateClientSideAsyncTaskStatus', {
            'id': client_side_task_id,
            'task_status': {
              'status': 'FAILURE',
              'value': 100,
              'msg': 'Failed to get analystic data',
            },
          })
        });
    },
    formatResults () {
      // Only keep max 50000 items (highest impressions)
      google_console_data_aggregated.sort((a, b) => {
        if (a.impressions == b.impressions) {
          if (a.keyword < b.keyword) return -1;
          if (a.keyword > b.keyword) return 1;
        } else {
          return Number(b.impressions) - Number(a.impressions);
        }
      });

      if (google_console_data_aggregated.length > 50000) {
        google_console_data_aggregated = google_console_data_aggregated.slice(0, 50000);
      }

      const google_data = google_console_data_aggregated.map((d) => {
        return {
          ...d,
          keyword: d.keys[0],
          page: d.keys[1],
          clicks: d.clicks,
          ctr: Number(Number(d.ctr * 100).toFixed(2)),
          position: Number(Number(d.position).toFixed(2))
        }
      })

      const google_data_by_page = groupBy(google_data, "page");

      let results = Object.entries(google_data_by_page).map(([key, records]) => ({
        page: key,
        impressions: sumBy(records, "impressions"),
        clicks: sumBy(records, "clicks"),
        avgCTR: Number(Number(
          meanBy(records, "ctr")
        ).toFixed(2)),
        avgPosition: Number(Number(
          meanBy(records, "position")
        ).toFixed(2)),
        keywordsCount: records.length,
      }))

      return results;
    },
    onStart() {
      this.setConfig('wizardPOIsCalculating', true)
      google_console_data_aggregated = []

      const client_side_task_id = Math.floor(Math.random() * Date.now());
      this.$store.commit('registerNewClientSideAsyncTask', {
        'id': client_side_task_id,
        'type': 'PageOpportunity.form',
        'task_status': {
          'status': 'PROGRESS',
          'msg': 'Processing',
          'value': 5
        },
      })

      this.setConfig('wizardPOInterval', setInterval(() => {
        this.$store.commit('updateClientSideAsyncTaskStatus', {
          'id': client_side_task_id,
          'task_status': {
            'status': 'PROGRESS',
            value: Math.min(this.$store.getters.clientSideAsyncTaskById(client_side_task_id).task_status.value + 1, 100),  // TODO
            msg: 'Processing...'
          },
        })
      }, 1000))

      this.getConsoleDataAggregated(0, client_side_task_id)
    },
  },
  mounted () {
    if (this.campaign) {
      if (!this.config.wizardPOLanguage) {
        this.setConfig('wizardPOLanguage', this.campaign.languageCode)
      }
      if(!this.config.wizardPOLocation) {
        this.setConfig('wizardPOLocation', parseInt(this.campaign.locationCode))
      }
    }
    if (!this.googleSiteUrl && this.config.wizardPODomain) {
      this.googleSiteUrl = this.config.wizardPODomain
    }
    if (!this.config.wizardPOPeriod) this.setConfig('wizardPOPeriod', this.availablePeriods[1])  // to give default value from the computed property
  }
}
</script>
  
<style lang="scss" scoped>
@import "../../../keyword-research.scss";

.white-glove-site-content-audit-oboarding-tip {
  position: absolute;
  top: -10px;
  left: 90px;
  z-index: 1;
}
</style>
